import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

type RetryableRequestConfig = AxiosRequestConfig & { _retry?: boolean };

type AuthInterceptorOptions = {
  getAuthHeader: () => Promise<string | undefined>;
  platformHeader: string;
};

export const authInterceptors = {
  install(
    instance: AxiosInstance,
    { getAuthHeader, platformHeader }: AuthInterceptorOptions
  ) {
    const requestInterceptor = instance.interceptors.request.use(
      async (config) => {
        const authorization =
          config.headers?.Authorization ?? (await getAuthHeader());

        config.headers = {
          ...config.headers,
          ...(authorization ? { Authorization: authorization } : undefined),
          'x-chatie-platform': platformHeader,
        };

        return config;
      }
    );

    const responseInterceptor = instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as
          | RetryableRequestConfig
          | undefined;
        if (!originalRequest) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const authorization = await getAuthHeader();
          if (authorization) {
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: authorization,
            };
            return instance(originalRequest);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      instance.interceptors.request.eject(requestInterceptor);
      instance.interceptors.response.eject(responseInterceptor);
    };
  },
};
